import { useEffect, useState } from 'react'
import { WifiOff } from 'lucide-react'
import { offlineCaptureAvailable } from '@/lib/offline/capability'
import { readStationCache } from '@/lib/offline/stationCache'
import { hasSilentPrintShell } from '@/lib/offline/shell'
import { useOnlineAndReachable } from '@/lib/offline/reachability'

/**
 * The offline banner.
 *
 * Its wording has to track what the app can ACTUALLY do. Before F-FE-N30 it
 * said new orders needed a connection, which was true. Now a prepared desktop
 * terminal — or, since F-FE-N123, a prepared Bhookly Server tablet — can take
 * orders and fire kitchen tickets offline, so saying that would be a lie — and
 * a banner that under-promises is as bad as one that over-promises: staff stop
 * taking orders they could have taken.
 *
 * So there are two messages, chosen by the same predicate that gates the
 * button. A terminal that is NOT prepared keeps the old, honest wording.
 */
export function OfflineIndicator() {
  // F-FE-N30: the banner must agree with the button. Both key on backend
  // reachability, not just navigator.onLine.
  const { online: osOnline, backendReachable } = useOnlineAndReachable()
  const online = osOnline && backendReachable
  const [canCapture, setCanCapture] = useState(false)

  useEffect(() => {
    if (online) return
    let cancelled = false
    void (async () => {
      const cache = await readStationCache()
      const cap = offlineCaptureAvailable({
        online: osOnline,
        backendReachable,
        canPrintSilently: hasSilentPrintShell(),
        stationsCachedAt: cache?.fetchedAt ?? null,
        hasKdsStation: cache?.hasKdsStation ?? false,
      })
      if (!cancelled) setCanCapture(cap.ok)
    })()
    return () => {
      cancelled = true
    }
  }, [online, osOnline, backendReachable])

  if (online) return null

  // F-FE-N127: the banner is inert and must never sit on the control it is
  // explaining. On a phone-width counter the bottom-centre of the screen is the
  // "Place order & Fire" button and its refusal hint; on the floor screen it is
  // "Fire KOT" at the foot of the ticket rail (a slide-in drawer below md, a
  // bottom sheet on a portrait tablet). The 2026-09-08 phone test read as
  // "blocked by the offline banner" because the pill covered exactly that.
  //
  // So: taps fall through (`pointer-events-none` — there is nothing to tap on
  // it), it docks at the TOP over a title row below the desktop breakpoint, and
  // only from lg up does it keep the bottom slot, where the counter's action
  // footer lives in the right-hand rail and the bottom-centre is product grid.
  //
  // F-FE-N153: the SHORT copy runs all the way to `lg`, not just to `md`. The
  // long line was two to four wrapped lines on a portrait tablet, and while it
  // docks at the top that reached down over the Menu / Ticket tabs, the "New
  // order" control and the order-type toggle. This comment already named that
  // failure — "four wrapped lines over the title row would reach the Menu /
  // Ticket tabs" — but the breakpoint that was supposed to prevent it stopped
  // at `md`, leaving every device between 768px and 1024px showing the long
  // copy in the one position where length is the whole problem. That is the
  // band a landscape 10" tablet sits in.
  return (
    <div className="pointer-events-none fixed left-1/2 top-3 z-[100] max-w-[calc(100vw-1.5rem)] -translate-x-1/2 lg:top-auto lg:bottom-3">
      <div className="flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 shadow-lg dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        <WifiOff className="h-4 w-4 shrink-0" />
        {canCapture ? (
          <>
            <span className="lg:hidden">Offline — orders &amp; kitchen tickets still work.</span>
            {/*
              F-FE-N153: it used to end "Payment resumes when you reconnect",
              which is false. A prepared terminal takes payment offline too
              (F-CTRL-N55) — from the order's card in the tray, after firing,
              not from the cart. The tenant read the banner, went looking for a
              Pay button in the cart, did not find one, and reported payment as
              broken. This component's own doc says a banner that
              under-promises is as bad as one that over-promises, and that is
              exactly what happened.

              It stays deliberately silent about WHERE payment lives, because
              this banner is global: the server floor screen can capture
              offline too and has no payment surface at all, so promising one
              there would trade this bug for its mirror image.
            */}
            <span className="hidden lg:inline">
              Offline — orders and kitchen tickets still work. Everything syncs when you reconnect.
            </span>
          </>
        ) : (
          <>
            <span className="lg:hidden">Offline — new orders need a connection.</span>
            <span className="hidden lg:inline">
              Offline — showing last saved menu. New orders and payments need a connection.
            </span>
          </>
        )}
      </div>
    </div>
  )
}
